import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, Printer } from 'lucide-react';
import { EmptyState } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { bankingKey, type ReconcileLine, type ReconciliationReport } from '@/lib/banking';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { useAccountNames } from '@/lib/accounts';

export const Route = createFileRoute('/banking/reconcile/$reconciliationId/report')({
  component: ReconciliationReportPage,
});

/** Screen B6 (document 06): reconciliation report, in the report page pattern. */
function ReconciliationReportPage() {
  const orgId = useOrgId();
  const { reconciliationId } = Route.useParams();
  const t = useT();
  const names = useAccountNames();
  const report = useQuery({
    queryKey: [...bankingKey(orgId), 'reconciliation-report', reconciliationId],
    queryFn: () =>
      api<ReconciliationReport>(
        `/orgs/${orgId}/banking/reconciliations/${reconciliationId}/report`,
      ),
    enabled: Boolean(orgId),
  });
  if (!orgId) return <EmptyState title={t('shell.selectClient')} />;
  if (report.isPending)
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (report.isError)
    return (
      <EmptyState
        title={t('purchasePages.reconcileReport.loadFailed')}
        hint={report.error.message}
      />
    );
  const r = report.data;
  const rec = r.reconciliation;
  const c = rec.currency;
  const count = (n: number) => t('purchasePages.reconcileReport.transactions', { count: n });
  const date = displayDate(rec.statementDate);

  return (
    <>
      <div className="mb-2 flex items-center justify-between print:hidden">
        <Link
          to="/banking/reconcile"
          search={{ accountId: rec.accountId }}
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="rtl:-scale-x-100 size-4" />{' '}
          {t('purchasePages.reconcileReport.back')}
        </Link>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('purchasePages.reconcileReport.print')}
          onClick={() => window.print()}
        >
          <Printer className="size-4" />
        </Button>
      </div>
      <div className="mx-auto max-w-5xl rounded-lg border bg-card px-8 py-6">
        <header className="mb-6 text-center">
          <div className="text-lg font-semibold">{r.orgName}</div>
          <div className="text-base">
            {t('purchasePages.reconcileReport.periodEnding', {
              code: rec.accountCode,
              name: names.name(rec.accountId, rec.accountName),
              date,
            })}
          </div>
          <div className="text-sm text-muted-foreground">
            {rec.status === 'finished'
              ? rec.finishedByName
                ? t('purchasePages.reconcileReport.reconciledOnBy', {
                    date: rec.finishedAt ? displayDate(rec.finishedAt.slice(0, 10)) : '',
                    name: rec.finishedByName,
                  })
                : t('purchasePages.reconcileReport.reconciledOn', {
                    date: rec.finishedAt ? displayDate(rec.finishedAt.slice(0, 10)) : '',
                  })
              : rec.status === 'undone'
                ? t('purchasePages.reconcileReport.undoneOn', {
                    date: rec.undoneAt ? displayDate(rec.undoneAt.slice(0, 10)) : '',
                    reason: rec.undoReason ?? '',
                  })
                : t('purchasePages.reconcileReport.inProgress')}
          </div>
        </header>

        <h2 className="mb-2 font-semibold">{t('purchasePages.reconcileReport.summary')}</h2>
        <table className="mb-8 w-full text-sm" data-testid="reconciliation-summary">
          <tbody>
            <SummaryRow
              label={t('purchasePages.reconcileReport.beginningBalance')}
              value={rec.beginningBalance}
              c={c}
            />
            <SummaryRow
              label={t('purchasePages.reconcileReport.clearedPaymentsCount', {
                count: count(r.clearedPayments.length),
              })}
              value={negate(r.totals.clearedPayments)}
              c={c}
              indent
            />
            <SummaryRow
              label={t('purchasePages.reconcileReport.clearedDepositsCount', {
                count: count(r.clearedDeposits.length),
              })}
              value={r.totals.clearedDeposits}
              c={c}
              indent
            />
            <SummaryRow
              label={t('purchasePages.reconcileReport.endingBalance')}
              value={rec.statementBalance}
              c={c}
              strong
            />
            <SummaryRow
              label={t('purchasePages.reconcileReport.unclearedPaymentsCount', {
                date,
                count: count(r.unclearedPayments.length),
              })}
              value={negate(r.totals.unclearedPayments)}
              c={c}
              indent
            />
            <SummaryRow
              label={t('purchasePages.reconcileReport.unclearedDepositsCount', {
                date,
                count: count(r.unclearedDeposits.length),
              })}
              value={r.totals.unclearedDeposits}
              c={c}
              indent
            />
            <SummaryRow
              label={t('purchasePages.reconcileReport.registerBalanceAsOf', { date })}
              value={r.totals.registerBalance}
              c={c}
              strong
            />
            <SummaryRow
              label={t('purchasePages.reconcileReport.newAfterCount', {
                date,
                count: count(r.unclearedAfter.length),
              })}
              value={null}
              c={c}
              indent
            />
            <SummaryRow
              label={t('purchasePages.reconcileReport.registerBalanceToday')}
              value={r.totals.registerBalanceToday}
              c={c}
              strong
            />
          </tbody>
        </table>

        <Section
          title={t('purchasePages.reconcileReport.clearedPayments')}
          lines={r.clearedPayments}
          side="payment"
        />
        <Section
          title={t('purchasePages.reconcileReport.clearedDeposits')}
          lines={r.clearedDeposits}
          side="deposit"
        />
        <Section
          title={t('purchasePages.reconcileReport.unclearedPayments', { date })}
          lines={r.unclearedPayments}
          side="payment"
        />
        <Section
          title={t('purchasePages.reconcileReport.unclearedDeposits', { date })}
          lines={r.unclearedDeposits}
          side="deposit"
        />
        <Section
          title={t('purchasePages.reconcileReport.newAfter', { date })}
          lines={r.unclearedAfter}
          side="both"
        />
        <footer className="mt-8 text-center text-xs text-muted-foreground">
          {t('purchasePages.reconcileReport.footer', {
            org: r.orgName,
            when: new Date(r.generatedAt).toLocaleString('en-GB', { hour12: false }),
          })}
        </footer>
      </div>
    </>
  );
}

const negate = (v: string) => (v === '0.00' ? v : v.startsWith('-') ? v.slice(1) : `-${v}`);

function SummaryRow({
  label,
  value,
  c,
  indent,
  strong,
}: {
  label: string;
  value: string | null;
  c: string;
  indent?: boolean;
  strong?: boolean;
}) {
  return (
    <tr className={strong ? 'border-t font-semibold' : ''}>
      <td className={`py-1.5 ${indent ? 'ps-6' : ''}`}>{label}</td>
      <td className="tnum w-[200px] py-1.5 text-end">
        {value === null ? '' : `${c} ${formatAmount(value)}`}
      </td>
    </tr>
  );
}

function Section({
  title,
  lines,
  side,
}: {
  title: string;
  lines: ReconcileLine[];
  side: 'payment' | 'deposit' | 'both';
}) {
  const t = useT();
  if (!lines.length) return null;
  return (
    <section className="mb-6">
      <h3 className="mb-1 text-sm font-semibold">{title}</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
            <th className="w-[110px] py-1.5 text-start font-medium">
              {t('purchasePages.reconcileReport.colDate')}
            </th>
            <th className="w-[140px] py-1.5 text-start font-medium">
              {t('purchasePages.reconcileReport.colType')}
            </th>
            <th className="w-[80px] py-1.5 text-start font-medium">
              {t('purchasePages.reconcileReport.colRef')}
            </th>
            <th className="py-1.5 text-start font-medium">
              {t('purchasePages.reconcileReport.colPayee')}
            </th>
            <th className="py-1.5 text-start font-medium">
              {t('purchasePages.reconcileReport.colMemo')}
            </th>
            <th className="w-[140px] py-1.5 text-end font-medium">
              {t('purchasePages.reconcileReport.colAmount')}
            </th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.journalLineId} className="border-b border-dashed last:border-0">
              <td className="tnum py-1">{displayDate(l.date)}</td>
              <td className="py-1">{l.type}</td>
              <td className="py-1">
                <Link
                  to="/accounting/journal-entries/$entryId"
                  params={{ entryId: l.entryId }}
                  className="text-primary hover:underline"
                >
                  {l.entryNo}
                </Link>
              </td>
              <td className="py-1">{l.payee ?? ''}</td>
              <td className="max-w-[280px] truncate py-1">{l.memo ?? ''}</td>
              <td className="tnum py-1 text-end">
                {side === 'payment' || (side === 'both' && l.payment)
                  ? formatAmount(`-${l.payment}`)
                  : formatAmount(l.deposit)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
